import React from "react";
import { Link } from "react-router";

import { ColumnDef } from "@tanstack/react-table";

import { enrichmentStatusColumn } from "@/components/common/status-columns";
import { UserAvatar } from "@/components/common/user-avatar";
import { PhotoWithBlurhash } from "@/components/media/photo-with-blurhash";
import { EditButton } from "@/components/refine-ui/buttons/edit";
import { ShowButton } from "@/components/refine-ui/buttons/show";
import { Badge } from "@/components/ui/badge";
import { makeTooltip } from "@/components/ui/tooltip";
import { useMembers } from "@/contexts/member-context";
import { parseUnixTimestamp } from "@/lib/dateUtils";
import { IPlayData } from "@/types/play";

export const usePlayColumns = () => {
  const { members } = useMembers();
  return React.useMemo<ColumnDef<IPlayData>[]>(
    () => [
      { id: "id", accessorKey: "document.id", header: "ID" },
      {
        id: "poster",
        header: "",
        accessorFn: (row) => row.document.media?.photos?.[0],
        enableSorting: false,
        enableColumnFilter: false,
        size: 56,
        cell: function render({ getValue }: any) {
          const photo = getValue();
          return photo?.url ? (
            <PhotoWithBlurhash
              className="h-12 w-8 rounded object-cover"
              url={photo.url}
              blurhash={photo.blurhash ?? ""}
            />
          ) : (
            <div className="h-12 w-8 rounded bg-muted" />
          );
        },
      },
      {
        id: "title",
        header: "Title",
        minSize: 200,
        size: 280,
        accessorFn: (row) =>
          row.highlights?.filter((h: any) => h.field === "title")[0]?.snippet ??
          row.document.title,
        cell: function render({ row, getValue }: any) {
          return makeTooltip(
            <Link
              to={`/plays/show/${row.original.document.id}`}
              className="block truncate font-medium hover:underline"
              dangerouslySetInnerHTML={{ __html: getValue() }}
            />,
          );
        },
        enableSorting: true,
      },
      {
        id: "playwright",
        header: "Playwright",
        size: 180,
        accessorFn: (row) => row.document.playwright ?? "—",
        enableSorting: false,
        enableColumnFilter: false,
        cell: function render({ getValue }: any) {
          return makeTooltip(<div className="truncate">{getValue()}</div>);
        },
      },
      {
        id: "source",
        header: "Source",
        size: 180,
        enableSorting: false,
        enableColumnFilter: false,
        // Renders an em-dash until PlayDbDto grows `metadata` (spec P2-b). Always
        // defined rather than conditionally hidden: visibility that flips between
        // pages is worse than a stable empty column.
        accessorFn: (row) => {
          const source = row.document.metadata?.source;
          const sourceId = row.document.metadata?.sourceId;
          return source || sourceId ? { source, sourceId } : null;
        },
        cell: function render({ getValue }: any) {
          const value = getValue();
          if (!value)
            return <span className="text-muted-foreground/60">—</span>;
          return makeTooltip(
            <div className="truncate">
              <span className="font-medium">{value.source ?? "—"}</span>
              {value.sourceId && (
                <span className="ml-1 text-muted-foreground">
                  {value.sourceId}
                </span>
              )}
            </div>,
            `${value.source ?? ""} ${value.sourceId ?? ""}`.trim(),
          );
        },
      },
      {
        id: "status",
        header: "Status",
        accessorKey: "document.status",
        enableSorting: false,
        size: 120,
        cell: function render({ getValue }: any) {
          const status = getValue();
          return status ? <Badge variant="default">{status}</Badge> : null;
        },
        meta: {
          filterType: "select",
          options: [
            { label: "Draft", value: "Draft" },
            { label: "Complete", value: "Complete" },
          ],
        },
      },
      // Immediately after Status — the shared definition, same as movies and
      // screenings. A play is NOT embeddable, so there is no embedding column.
      enrichmentStatusColumn<IPlayData>(),
      {
        id: "genres",
        header: "Genres",
        accessorFn: (row) =>
          (row.document.genres ?? []).map((g) => g.name).join(", "),
        cell: function render({ getValue }: any) {
          return makeTooltip(<div className="truncate">{getValue()}</div>);
        },
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        id: "createdBy",
        header: "Created By",
        minSize: 170,
        accessorKey: "document.createdBy",
        cell: function render({ getValue }: any) {
          const value = getValue();
          const member = members.find(
            (x) =>
              x.id === value || x.adminMetadata?.relatedIds?.includes(value),
          );
          return member ? (
            <div className="p-1">
              <UserAvatar {...member} half />
            </div>
          ) : (
            value
          );
        },
        enableSorting: false,
        // Unlike movies' collection, the plays Typesense collection does not
        // index `createdBy` — filtering on it 400s the whole list.
        enableColumnFilter: false,
      },
      {
        id: "createdAt",
        header: "Created At",
        // `createdAt` is optional on IPlay, and `parseUnixTimestamp(undefined)` is both a
        // type error and an "Invalid Date" cell. Read it as null and render an em-dash.
        accessorFn: (row) =>
          row.document.createdAt == null
            ? null
            : parseUnixTimestamp(row.document.createdAt),
        cell: function render({ getValue }: any) {
          const value = getValue();
          if (!value)
            return <span className="text-muted-foreground/60">—</span>;
          return makeTooltip(value.format("DD/MM/YYYY HH:mm"));
        },
        enableColumnFilter: true,
        meta: { filterType: "date" },
      },
      {
        id: "actions",
        header: "Actions",
        enableSorting: false,
        enablePinning: true,
        size: 100,
        cell: function render({ row }) {
          return (
            <div className="flex items-center gap-1">
              <ShowButton
                size="sm"
                hideText
                recordItemId={row.original.document.id}
              />
              <EditButton
                size="sm"
                hideText={true}
                recordItemId={row.original.document.id}
              />
            </div>
          );
        },
      },
    ],
    [members],
  );
};
